/**
 * Performance Manager for Aggressive JavaScript Optimization
 * 
 * This module manages third-party scripts with ultra-aggressive loading strategies
 * to minimize JavaScript execution time and improve Core Web Vitals.
 */

class PerformanceManager {
  private isIdle = false;
  private scriptsLoaded = new Set<string>();
  private loadQueue: Array<() => Promise<void>> = [];
  private userInteracted = false;
  private pageFullyLoaded = false;

  constructor() {
    if (typeof window !== 'undefined') {
      try {
        this.setupPerformanceOptimizations();
      } catch (error) {
        console.warn('Error setting up performance optimizations:', error);
      }
    }
  }

  private setupPerformanceOptimizations() {
    // Wait for page to be fully loaded before loading any non-critical scripts
    if (document.readyState === 'complete') {
      this.pageFullyLoaded = true;
      this.scheduleIdleLoading();
    } else {
      window.addEventListener('load', () => {
        this.pageFullyLoaded = true;
        this.scheduleIdleLoading();
      });
    }

    // Setup user interaction detection
    this.setupUserInteractionDetection();
  }

  private scheduleIdleLoading() {
    // Use multiple strategies to detect idle time
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.isIdle = true;
        this.processLoadQueue();
      }, { timeout: 10000 });
    } else {
      // Fallback: wait 8 seconds after page load
      setTimeout(() => {
        this.isIdle = true;
        this.processLoadQueue();
      }, 8000);
    }
  }

  private setupUserInteractionDetection() {
    const events: (keyof DocumentEventMap)[] = ['mousedown', 'touchstart', 'keydown', 'scroll'];
    
    const handleInteraction = () => {
      try {
        if (!this.userInteracted) {
          this.userInteracted = true;
          // Load scripts after user interaction with delay
          setTimeout(() => {
            this.isIdle = true;
            this.processLoadQueue();
          }, 2000);
        }
        
        // Remove listeners after first interaction
        events.forEach(eventType => {
          document.removeEventListener(eventType, handleInteraction);
        });
      } catch (error) {
        console.warn('Error in user interaction handler:', error);
      }
    };

    events.forEach(event => {
      try {
        document.addEventListener(event, handleInteraction, { passive: true });
      } catch (error) {
        console.warn(`Error adding event listener for ${event}:`, error);
      }
    });
  }

  private async processLoadQueue() {
    if (!this.isIdle || !this.pageFullyLoaded) return;
    
    // Process queue with delays to avoid blocking
    while (this.loadQueue.length > 0) {
      const loadScript = this.loadQueue.shift();
      if (loadScript) {
        try {
          await loadScript();
          // Add small delay between script loads
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.warn('Failed to load script:', error);
        }
      }
    }
  }

  private async loadScript(src: string, id: string, priority: 'low' | 'medium' | 'high' = 'low'): Promise<void> {
    if (this.scriptsLoaded.has(id)) return;
    if (typeof window === 'undefined') return;

    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        
        // Add priority-based loading
        if (priority === 'high') {
          script.setAttribute('data-priority', 'high');
        }
        
        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          console.warn(`Script loading timeout for ${src}`);
          reject(new Error(`Script loading timeout: ${src}`));
        }, 10000); // 10 second timeout
        
        script.onload = () => {
          clearTimeout(timeout);
          this.scriptsLoaded.add(id);
          resolve();
        };
        script.onerror = (error) => {
          clearTimeout(timeout);
          console.warn(`Failed to load script ${src}:`, error);
          reject(new Error(`Failed to load script: ${src}`));
        };
        
        document.head.appendChild(script);
      } catch (error) {
        console.warn(`Error creating script element for ${src}:`, error);
        reject(error);
      }
    });
  }

  public async loadGTM(): Promise<void> {
    const id = 'gtm';
    if (this.scriptsLoaded.has(id)) return;

    try {
      // Initialize dataLayer if not exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        
        // Initialize GTM before loading script
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });
      }
      
      await this.loadScript('https://www.googletagmanager.com/gtm.js?id=GTM-K8WLQLF4', id, 'low');
    } catch (error) {
      console.warn('GTM loading failed, will retry later:', error);
      // Retry after 5 seconds
      setTimeout(() => {
        if (!this.scriptsLoaded.has(id)) {
          this.loadGTM();
        }
      }, 5000);
    }
  }

  // Amplitude is loaded and initialized by amplitudeLoader.ts — no duplicate load here
  public async loadAmplitude(): Promise<void> {
    // no-op: handled by amplitudeLoader.ts with custom config (pageViews: false)
  }

  // GA4 is managed entirely through GTM — no direct install needed
  public async loadGoogleAnalytics(): Promise<void> {
    // no-op: GA4 fires via GTM tags, not directly on the site
  }
}

// Export singleton instance
export const performanceManager = new PerformanceManager();

// Convenience functions
export const loadGTMOptimized = () => performanceManager.loadGTM();
export const loadAmplitudeOptimized = () => performanceManager.loadAmplitude();
export const loadGoogleAnalyticsOptimized = () => performanceManager.loadGoogleAnalytics();
